import { Spinner } from "react-bootstrap";
import { forwardRef, useEffect, useState } from "react";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { PageLoader } from "../../../Components/NoDataFoundTable/NoDataFoundTable";

const FilePreview = forwardRef(({ FileUrl, HeightData }, ref) => {
  const defaultLayoutPluginInstance = defaultLayoutPlugin();
  const [fileType, setFileType] = useState("");
  const [loading, setLoading] = useState(false);
  const [textContent, setTextContent] = useState("");

  useEffect(() => {
    if (FileUrl) {
      const extension = FileUrl.split(".").pop().toLowerCase();
      setFileType(extension);
      setTextContent("");
      setLoading(false);

      if (["txt", "js", "json", "css", "html", "md"].includes(extension)) {
        setLoading(true);
        fetch(FileUrl)
          .then((res) => res.text())
          .then((text) => {
            setTextContent(text);
            setLoading(false);
          })
          .catch(() => setLoading(false));
      }
    }
  }, [FileUrl]);

  const renderPreview = () => {
    if (!FileUrl) return <p className="p-3 text-muted">No file selected</p>;
    
    if (["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(fileType)) {
      return (
        <div className="hstack justify-content-center h-100 p-3">
          <img
            src={FileUrl}
            alt="preview"
            className="w-100 shadow-sm"
          />
        </div>
      );
    }
    
    if (fileType === "pdf") {
      return (
        <Worker workerUrl="https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js">
          <Viewer
            fileUrl={FileUrl}
            plugins={[defaultLayoutPluginInstance]}
            onError={(error) => console.error("Error loading PDF:", error)}
          />
        </Worker>
      );
    }

    if (["mp4", "webm", "ogg", "mov", "avi", "mkv"].includes(fileType)) {
      return (
        <div className="d-flex justify-content-center align-items-center h-100 p-3">
          <video
            controls
            className="w-100 rounded shadow-sm"
            style={{ maxHeight: "80vh" }}
          >
            <source src={FileUrl} type={`video/${fileType}`} />
            Your browser does not support the video tag.
          </video>
        </div>
      );
    }

    if (["mp3", "wav", "ogg", "m4a"].includes(fileType)) {
      return (
        <div className="d-flex justify-content-center align-items-center h-100 p-3">
          <audio controls style={{ width: "100%" }}>
            <source src={FileUrl} type={`audio/${fileType}`} />
            Your browser does not support the audio tag.
          </audio>
        </div>
      );
    }

    if (["txt", "js", "jsx", "json", "css", "html", "md"].includes(fileType)) {
      if (loading)
        return (
          <div className="hstack justify-content-center h-100">
            <PageLoader />
          </div>
        );

      return (
        <pre
          style={{
            padding: "1rem",
            overflow: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            margin: 0,
          }}
        >
          {textContent || "No content to display."}
        </pre>
      );
    }

    if (["doc", "docx", "ppt", "pptx"].includes(fileType)) {
      const viewerUrl =
        fileType === "ppt" || fileType === "pptx"
          ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(FileUrl)}`
          : `https://docs.google.com/gview?url=${encodeURIComponent(FileUrl)}&embedded=true`;

      return (
        <div className="overflow-hidden h-100">
          <iframe
            src={viewerUrl}
            title="Office File Preview"
            width="100%"
            height="100%"
            style={{ border: "none" }}
          />
        </div>
      );
    }


    return (
      <div className="p-3 hstack justify-content-center h-100">
        <div className="text-center text-muted">
          <p>Preview not available for this file type.</p>
          <a href={FileUrl} target="_blank" rel="noopener noreferrer">
            Download File
          </a>
        </div>
      </div>
    );
  };

  return (
    <div ref={ref} className="p-0" style={{height: HeightData}}>
      {renderPreview()}
    </div>
  );
});

export default FilePreview;
